"use client"

import { useRef, useEffect } from "react"
import Link from "next/link"
import Image from "next/image"
import { LogOut, Settings, User } from "lucide-react"

type SessionLike = {
  user?: {
    username?: string | null
    role?: string | null
    avatarUrl?: string | null
  } | null
} | null

export function UserMenu({
  session,
  isLoading,
  isOpen,
  setIsOpen,
  onSignOut,
}: {
  session: SessionLike
  isLoading: boolean
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  onSignOut: () => void
}) {
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [setIsOpen])

  return (
    <div className="relative" ref={userMenuRef}>
      {isLoading ? (
        <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-zinc-800 animate-pulse" />
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 group transition-all outline-none"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          <div className="relative">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden border border-gray-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 shadow-sm group-hover:border-blue-500/50 transition-colors">
              {session?.user?.avatarUrl ? (
                <Image src={session.user.avatarUrl} alt="Avatar" fill className="object-cover object-center rounded-xl" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-blue-500/10 text-blue-600">
                  <User className="w-5 h-5" />
                </div>
              )}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-zinc-900 shadow-sm" />
          </div>
        </button>
      )}

      {isOpen && (
        <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-zinc-800 py-2 animate-in fade-in slide-in-from-top-2 z-50">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-zinc-800">
            <p className="text-sm font-bold dark:text-white truncate">{session?.user?.username}</p>
            <p className="text-[10px] text-gray-500 dark:text-zinc-500 uppercase font-bold tracking-wider mt-0.5">{session?.user?.role}</p>
          </div>
          <div className="p-1.5 space-y-0.5">
            <Link href="/profile" className="flex items-center gap-3 px-3 py-2 text-sm rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
              <User className="w-4 h-4 text-gray-500" /> Twój Profil
            </Link>
            <Link href="/settings" className="flex items-center gap-3 px-3 py-2 text-sm rounded-xl hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
              <Settings className="w-4 h-4 text-gray-500" /> Ustawienia
            </Link>
            <div className="h-px bg-gray-100 dark:bg-zinc-800 my-1 mx-2" />
            <button
              onClick={onSignOut}
              className="flex w-full items-center gap-3 px-3 py-2 text-sm text-red-500 rounded-xl hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Wyloguj się
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
